import { useEffect } from 'react';
import type { PublicPageSeo } from './seoConfig';
import { buildHomePageJsonLd } from './structuredData';
import { DEFAULT_OG_IMAGE, SITE_NAME, canonicalUrl } from './site';

const JSON_LD_ID = 'ridemaadi-jsonld';

function upsertMeta(
  selector: string,
  create: () => HTMLElement,
  content: string,
): HTMLElement {
  let el = document.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertLink(rel: string, href: string): HTMLLinkElement {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

/** Apply title, description, canonical, Open Graph, and Twitter metadata for a public page. */
export function usePublicPageSeo(page: PublicPageSeo) {
  useEffect(() => {
    const previousTitle = document.title;
    const canonical = canonicalUrl(page.path);

    document.title = page.title;

    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';
    if (descriptionMeta) descriptionMeta.setAttribute('content', page.description);

    const canonicalLink = upsertLink('canonical', canonical);

    const managed: HTMLElement[] = [
      upsertMeta(
        'meta[property="og:title"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:title');
          return m;
        },
        page.title,
      ),
      upsertMeta(
        'meta[property="og:description"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:description');
          return m;
        },
        page.description,
      ),
      upsertMeta(
        'meta[property="og:url"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:url');
          return m;
        },
        canonical,
      ),
      upsertMeta(
        'meta[property="og:type"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:type');
          return m;
        },
        'website',
      ),
      upsertMeta(
        'meta[property="og:site_name"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:site_name');
          return m;
        },
        SITE_NAME,
      ),
      upsertMeta(
        'meta[property="og:image"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:image');
          return m;
        },
        DEFAULT_OG_IMAGE,
      ),
      upsertMeta(
        'meta[property="og:locale"]',
        () => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'og:locale');
          return m;
        },
        'en_IN',
      ),
      upsertMeta(
        'meta[name="twitter:card"]',
        () => {
          const m = document.createElement('meta');
          m.name = 'twitter:card';
          return m;
        },
        'summary_large_image',
      ),
      upsertMeta(
        'meta[name="twitter:title"]',
        () => {
          const m = document.createElement('meta');
          m.name = 'twitter:title';
          return m;
        },
        page.title,
      ),
      upsertMeta(
        'meta[name="twitter:description"]',
        () => {
          const m = document.createElement('meta');
          m.name = 'twitter:description';
          return m;
        },
        page.description,
      ),
      upsertMeta(
        'meta[name="twitter:image"]',
        () => {
          const m = document.createElement('meta');
          m.name = 'twitter:image';
          return m;
        },
        DEFAULT_OG_IMAGE,
      ),
    ];

    const robotsMeta = document.querySelector('meta[name="robots"]');
    if (robotsMeta) robotsMeta.remove();

    let jsonLdEl = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
    if (page.path === '/') {
      if (!jsonLdEl) {
        jsonLdEl = document.createElement('script');
        jsonLdEl.id = JSON_LD_ID;
        jsonLdEl.type = 'application/ld+json';
        document.head.appendChild(jsonLdEl);
      }
      jsonLdEl.textContent = JSON.stringify(buildHomePageJsonLd(page.description));
    } else if (jsonLdEl) {
      jsonLdEl.remove();
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
      canonicalLink.remove();
      managed.forEach((el) => el.remove());
      document.getElementById(JSON_LD_ID)?.remove();
    };
  }, [page.path, page.title, page.description]);
}
